// ─────────────────────────────────────────────────────────────
// Configuração
// ─────────────────────────────────────────────────────────────
const API_BASE_URL     = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const COGNITO_REGION   = process.env.REACT_APP_COGNITO_REGION   || 'us-east-1';
const USER_POOL_ID     = process.env.REACT_APP_USER_POOL_ID     || '';
const CLIENT_ID        = process.env.REACT_APP_COGNITO_CLIENT_ID || '';
const COGNITO_ENDPOINT = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com`;

// ─────────────────────────────────────────────────────────────
// Helpers de token (localStorage)
// ─────────────────────────────────────────────────────────────
export const getTokens = () => ({
  idToken:      localStorage.getItem('idToken'),
  accessToken:  localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  expiresAt:    parseInt(localStorage.getItem('tokenExpiresAt') || '0', 10)
});

export const saveTokens = ({ idToken, accessToken, refreshToken, expiresIn = 3600 }) => {
  localStorage.setItem('idToken',         idToken);
  localStorage.setItem('accessToken',     accessToken);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('tokenExpiresAt',  Date.now() + expiresIn * 1000);
};

export const clearTokens = () => {
  ['idToken', 'accessToken', 'refreshToken', 'tokenExpiresAt', 'empresaId', 'userEmail'].forEach(
    k => localStorage.removeItem(k)
  );
};

const isTokenExpired = () => {
  const { expiresAt } = getTokens();
  return Date.now() > expiresAt - 60_000; // renovar 1 min antes
};

// ─────────────────────────────────────────────────────────────
// Cognito — chamada direta à API REST (sem Amplify)
// ─────────────────────────────────────────────────────────────
const cognitoRequest = async (action, body) => {
  const res = await fetch(COGNITO_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type':  'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`
    },
    body: JSON.stringify(body)
  });
  const data = await res.json();
  if (!res.ok) throw { code: data.__type, message: data.message };
  return data;
};

// ─────────────────────────────────────────────────────────────
// Auth — Login (1a etapa: usuário + senha)
// ─────────────────────────────────────────────────────────────
export const signIn = async (email, password) => {
  const data = await cognitoRequest('InitiateAuth', {
    AuthFlow:       'USER_PASSWORD_AUTH',
    ClientId:       CLIENT_ID,
    AuthParameters: { USERNAME: email, PASSWORD: password }
  });

  // Novo usuário com senha temporária
  if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
    return { challenge: 'NEW_PASSWORD_REQUIRED', session: data.Session };
  }

  // MFA por e-mail (CustomEmailSender)
  if (data.ChallengeName === 'SOFTWARE_TOKEN_MFA' ||
      data.ChallengeName === 'CUSTOM_CHALLENGE') {
    return { challenge: data.ChallengeName, session: data.Session };
  }

  // Login direto (sem MFA) — não deve ocorrer em prod
  if (data.AuthenticationResult) {
    saveTokens({
      idToken:      data.AuthenticationResult.IdToken,
      accessToken:  data.AuthenticationResult.AccessToken,
      refreshToken: data.AuthenticationResult.RefreshToken,
      expiresIn:    data.AuthenticationResult.ExpiresIn
    });
    return { challenge: null };
  }

  throw { code: 'UnknownChallenge', message: `Challenge inesperado: ${data.ChallengeName}` };
};

// ─────────────────────────────────────────────────────────────
// Auth — Confirmar MFA
// ─────────────────────────────────────────────────────────────
export const confirmMFA = async (email, code, session, challengeName = 'SOFTWARE_TOKEN_MFA') => {
  const data = await cognitoRequest('RespondToAuthChallenge', {
    ClientId:          CLIENT_ID,
    ChallengeName:     challengeName,
    Session:           session,
    ChallengeResponses: {
      USERNAME:               email,
      SOFTWARE_TOKEN_MFA_CODE: code,
      ANSWER:                 code  // para CUSTOM_CHALLENGE
    }
  });

  if (data.AuthenticationResult) {
    saveTokens({
      idToken:      data.AuthenticationResult.IdToken,
      accessToken:  data.AuthenticationResult.AccessToken,
      refreshToken: data.AuthenticationResult.RefreshToken,
      expiresIn:    data.AuthenticationResult.ExpiresIn
    });
    return true;
  }
  throw { code: 'MFAFailed', message: 'Código inválido' };
};

// ─────────────────────────────────────────────────────────────
// Auth — Nova senha (primeiro acesso)
// ─────────────────────────────────────────────────────────────
export const setNewPassword = async (email, newPassword, session) => {
  const data = await cognitoRequest('RespondToAuthChallenge', {
    ClientId:           CLIENT_ID,
    ChallengeName:      'NEW_PASSWORD_REQUIRED',
    Session:            session,
    ChallengeResponses: { USERNAME: email, NEW_PASSWORD: newPassword }
  });

  if (data.ChallengeName) {
    return { challenge: data.ChallengeName, session: data.Session };
  }
  if (data.AuthenticationResult) {
    saveTokens({
      idToken:      data.AuthenticationResult.IdToken,
      accessToken:  data.AuthenticationResult.AccessToken,
      refreshToken: data.AuthenticationResult.RefreshToken,
      expiresIn:    data.AuthenticationResult.ExpiresIn
    });
    return { challenge: null };
  }
};

// ─────────────────────────────────────────────────────────────
// Auth — Refresh automático de token
// ─────────────────────────────────────────────────────────────
export const refreshTokens = async () => {
  const { refreshToken } = getTokens();
  if (!refreshToken) throw new Error('Sem refresh token');

  const data = await cognitoRequest('InitiateAuth', {
    AuthFlow:       'REFRESH_TOKEN_AUTH',
    ClientId:       CLIENT_ID,
    AuthParameters: { REFRESH_TOKEN: refreshToken }
  });

  if (data.AuthenticationResult) {
    saveTokens({
      idToken:     data.AuthenticationResult.IdToken,
      accessToken: data.AuthenticationResult.AccessToken,
      expiresIn:   data.AuthenticationResult.ExpiresIn
    });
    return data.AuthenticationResult.IdToken;
  }
  throw new Error('Falha ao renovar token');
};

// ─────────────────────────────────────────────────────────────
// Auth — Logout
// ─────────────────────────────────────────────────────────────
export const signOut = () => clearTokens();

// ─────────────────────────────────────────────────────────────
// authFetch — fetch autenticado com refresh automático
// ─────────────────────────────────────────────────────────────
export const authFetch = async (path, options = {}) => {
  let { idToken } = getTokens();

  // Renovar token se expirado
  if (isTokenExpired()) {
    try { idToken = await refreshTokens(); }
    catch { clearTokens(); window.location.href = '/'; throw new Error('Sessão expirada'); }
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type':  'application/json',
      'Authorization': idToken,
      ...options.headers
    }
  });

  if (res.status === 401) {
    clearTokens();
    window.location.href = '/';
    throw new Error('Não autorizado');
  }

  return res;
};

// ─────────────────────────────────────────────────────────────
// API — Upload
// ─────────────────────────────────────────────────────────────
export const getPresignedUrl = async (empresaId, filename) => {
  // filename original na key S3 — sem encode
  const s3Key = `pending/${empresaId}/${filename}`;
  // apenas o empresaId e a key completa são encodados na query string
  const res = await authFetch(
    `/upload/presigned?empresaId=${encodeURIComponent(empresaId)}&key=${encodeURIComponent(s3Key)}&bucket=uploads`
  );
  if (!res.ok) throw new Error('Erro ao obter URL de upload');
  return res.json();
};

export const uploadFileToS3 = async (presignedUrl, file) => {
  const res = await fetch(presignedUrl, {
    method: 'PUT',
    body:   file
  });
  if (!res.ok) throw new Error('Erro ao enviar arquivo para o S3');
};

export const processUpload = async (empresaId, s3Key) => {
  const res = await authFetch('/upload', {
    method: 'POST',
    body:   JSON.stringify({ empresaId, s3Key })
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Erro ao processar'); }
  return res.json();
};

// ─────────────────────────────────────────────────────────────
// API — Multas
// ─────────────────────────────────────────────────────────────
export const getAllFines = async (empresaId, filters = {}) => {
  const params = new URLSearchParams({ empresaId, ...filters });
  const res = await authFetch(`/fines?${params}`);
  if (!res.ok) throw new Error('Erro ao buscar multas');
  const data = await res.json();
  return data.fines || [];
};

export const getFineByAuto = async (empresaId, auto) => {
  const res = await authFetch(`/fines?empresaId=${encodeURIComponent(empresaId)}&auto=${auto}`);
  if (!res.ok) throw new Error('Erro ao buscar auto');
  const data = await res.json();
  return (data.fines || [])[0] || null;
};

export const updateFineStatus = async (empresaId, auto, status) => {
  const res = await authFetch(`/fines/${auto}/status`, {
    method: 'PUT',
    body:   JSON.stringify({ empresaId, status })
  });
  if (!res.ok) throw new Error('Erro ao atualizar status');
  return res.json();
};

// ─────────────────────────────────────────────────────────────
// API — Uploads metadata (último arquivo processado)
// ─────────────────────────────────────────────────────────────
export const listFiles = async (empresaId) => {
  const res = await authFetch(`/files?empresaId=${encodeURIComponent(empresaId)}`);
  if (!res.ok) throw new Error('Erro ao buscar arquivos');
  return res.json();
};

// ─────────────────────────────────────────────────────────────
// API — Consolidated (dashboard)
// ─────────────────────────────────────────────────────────────
export const getConsolidated = async (empresaId) => {
  const res = await authFetch(`/consolidated?empresaId=${encodeURIComponent(empresaId)}`);
  if (!res.ok) throw new Error('Erro ao buscar dados consolidados');
  return res.json();
};

// ─────────────────────────────────────────────────────────────
// API — Recursos (appeals)
// ─────────────────────────────────────────────────────────────
export const getAppeals = async (empresaId) => {
  const res = await authFetch(`/appeals?empresaId=${encodeURIComponent(empresaId)}`);
  if (!res.ok) throw new Error('Erro ao buscar recursos');
  const data = await res.json();
  return data.appeals || [];
};

export const createAppeal = async (empresaId, auto, fase = 'defesa_previa') => {
  const res = await authFetch('/appeals', {
    method: 'POST',
    body:   JSON.stringify({ empresaId, auto, fase })
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Erro ao criar recurso'); }
  return res.json();
};

// ─────────────────────────────────────────────────────────────
// API — Defesas
// ─────────────────────────────────────────────────────────────
export const generateDefenseFirst = async (empresaId, auto) => {
  const res = await authFetch('/defense/first', {
    method: 'POST',
    body:   JSON.stringify({ empresaId, auto })
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Erro ao gerar defesa'); }
  return res.json(); // retorna { preview, s3_key }
};

export const generateDefenseSecond = async (empresaId, auto, respostaJuizS3Key) => {
  const res = await authFetch('/defense/second', {
    method: 'POST',
    body:   JSON.stringify({ empresaId, auto, respostaJuizS3Key })
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Erro ao gerar defesa'); }
  return res.json();
};

export const saveDefenseEdit = async (empresaId, auto, instancia, texto) => {
  // Salva via presigned URL — mesmo bucket documents
  const filename = `defesa-${instancia}a-instancia-editada.txt`;
  const { url, key } = await authFetch(
    `/upload/presigned?empresaId=${encodeURIComponent(empresaId)}&key=empresas/${empresaId}/autos/${auto}/${filename}&bucket=documents`
  ).then(r => r.json());

  await fetch(url, {
    method:  'PUT',
    body:    new Blob([texto], { type: 'text/plain' }),
    headers: { 'Content-Type': 'text/plain; charset=utf-8' }
  });

  return key;
};

export const downloadDefense = async (empresaId, auto, instancia = '1') => {
  const res = await authFetch(
    `/defense/download?empresaId=${encodeURIComponent(empresaId)}&auto=${auto}&instancia=${instancia}`
  );
  if (!res.ok) throw new Error('Erro ao obter link de download');
  return res.json(); // retorna { url, read_url, filename }
};

export const getDefenseText = async (readUrl) => {
  // Carrega o texto completo da defesa via presigned URL
  const res = await fetch(readUrl);
  if (!res.ok) throw new Error('Erro ao carregar texto da defesa');
  return res.text();
};

export const getAutoPdfUrl = async (empresaId, auto) => {
  const res = await authFetch(
    `/defense/download?empresaId=${encodeURIComponent(empresaId)}&auto=${auto}&tipo=auto_pdf`
  );
  if (!res.ok) throw new Error('Erro ao obter PDF do auto');
  return res.json(); // retorna { url, page }
};

// ─────────────────────────────────────────────────────────────
// Compatibilidade — uploadFile usado pelo FineManagement.js
// Encapsula o fluxo: presigned URL → PUT S3 → processUpload
// ─────────────────────────────────────────────────────────────
export const uploadFile = async (file, empresaId) => {
  const id = empresaId || localStorage.getItem('empresaId') || 'default';
  const { url, key } = await getPresignedUrl(id, file.name);
  await uploadFileToS3(url, file);
  return processUpload(id, key);
};

export const uploadRespostaJuiz = async (empresaId, auto, file) => {
  const filename = `resposta-juiz.txt`;
  const { url, key } = await authFetch(
    `/upload/presigned?empresaId=${encodeURIComponent(empresaId)}&key=empresas/${empresaId}/autos/${auto}/${filename}&bucket=documents&contentType=text/plain`
  ).then(r => r.json());

  await fetch(url, {
    method:  'PUT',
    body:    file,
    headers: { 'Content-Type': 'text/plain' }
  });

  return key;
};